// One mounted game at a time. Guest delegates unchanged; student uses isolated RAM.
import { ensureOutboxWritable, outboxHasRoom, restorePending, persistPending, acknowledgePending, markPendingConflict, discardObsolete, readPending } from './StudentProgressOutbox';
import { resolveCommercialControl } from '../../accountsEnvironment';
import { PlayerData } from './PlayerData';
import { createDefaultAvatar, studentAvatarBase } from '../avatar/AvatarConfig';
export const GAME_CONTENT_VERSION = '2026.10.0';
let active = null;
const clone = value => JSON.parse(JSON.stringify(value));
const status = session => ({ studentId: session?.student.student_id, pending: (session?.queue.length || 0) + (session?.review || 0),
  saving: !!session?.flight || !!session?.recoveryFlight, recovering: !!session?.recovering || !!session?.needsHydration, generation: session?.generation || 0, error: !!session?.error, conflict: !!session?.conflict,
  blocked: !!session?.recovering || !!session?.needsHydration || !!session?.storageError || !!session?.conflict || !!session?.full || !!session?.review, obsolete: session?.obsolete || 0, commercial: !!session?.commercial, review: session?.review || 0 });
const notify = () => {
  if (active) { try { active.full = !outboxHasRoom(active.student.student_id); } catch { active.storageError = true; } }
  window.dispatchEvent(new CustomEvent('tmag:progress-status', { detail: status(active) }));
};
export const getStudentProgressStatus = () => status(active);
export function snapshotToLocal(snapshot) {
  const data = clone(snapshot);
  for (const pm of Object.values(data.missionProgress || {})) for (const attempt of Object.values(pm.attempts || {})) { attempt.accuracy *= 100; attempt.date = Date.parse(attempt.date); }
  for (const skill of Object.values(data.skillProgress || {})) skill.date = Date.parse(skill.date);
  data.best = data.overallBest ? { ...data.overallBest, date: Date.parse(data.overallBest.date) } : null;
  if (data.lastRun) { data.lastRun.accuracy *= 100; data.lastRun.practiceMode = false; }
  return data;
}
function eventId() {
  if (crypto.randomUUID) return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16)); bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map(n => n.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
}
// Capture the existing authenticated request client/context, never credentials.
// Its closure prevents a late authorization response from activating another child.
export function getStudentPlaySession(studentId) {
  const session = active, generation = session?.generation;
  if (!session || session.closed || session.student.student_id !== studentId) throw new Error('Student session unavailable');
  return {
    context: session.context,
    student: clone(session.student),
    isCurrent: () => active === session && !session.closed && session.generation === generation && !session.recovering,
    request: session.request,
    flush: async () => {
      await flushSession(session);
      if (status(session).blocked) throw new Error('Pending progress must be recovered');
    },
  };
}
export function isCurrentProgressAttempt(id) {
  return !id || (!!active && !active.closed && active.playAttempt?.id === id &&
    active.playAttempt.generation === active.generation && !active.playAttempt.finished);
}
export const createProgressAttemptId = eventId;
function validateSnapshot(studentId, response, context) {
  if (response?.student_id !== studentId || !response?.snapshot || response.snapshot.schemaVersion !== 1 || response.snapshot.gameContentVersion !== GAME_CONTENT_VERSION || !/^[a-f0-9]{64}$/.test(response.context) || (context && response.context !== context) || !Number.isInteger(response.snapshot.revision) || response.snapshot.revision < 0) throw new Error("Invalid progress response");
}
export function activateStudentProgress(student, response, requestFn) {
  validateSnapshot(student.student_id, response);
  let storageError = false;
  try { ensureOutboxWritable(); } catch { storageError = true; }
  const commercial = resolveCommercialControl({
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT,
    REACT_APP_ACCOUNTS_V1_ENABLED: process.env.REACT_APP_ACCOUNTS_V1_ENABLED,
    REACT_APP_ACCOUNTS_COMMERCIAL_ENABLED: process.env.REACT_APP_ACCOUNTS_COMMERCIAL_ENABLED,
    REACT_APP_ACCOUNTS_API_URL: process.env.REACT_APP_ACCOUNTS_API_URL,
    REACT_APP_ACCOUNTS_FRONTEND_ORIGIN: process.env.REACT_APP_ACCOUNTS_FRONTEND_ORIGIN,
    REACT_APP_PAYPAL_C3C_ENABLED: process.env.REACT_APP_PAYPAL_C3C_ENABLED,
  });
  const recovered = restorePending(student.student_id, response.context);
  const session = { student, context: response.context, revision: response.snapshot.revision, data: snapshotToLocal(response.snapshot), request: requestFn, skills: {}, queue: recovered.compatible.map(record => record.attempt), conflict: recovered.compatible.some(record => record.state === 'blocked'), obsolete: recovered.obsolete.length, full: !outboxHasRoom(student.student_id), storageError, flight: null, error: null, closed: false, recovering: false, recoveryFlight: null, generation: 0, rejected: new Map() };
  for (const record of recovered.compatible) if (record.state === 'blocked') session.rejected.set(record.attempt.client_session_id, record.attempt);
  session.commercial = commercial; session.origins = new Map(); session.review = 0;
  if (commercial) {
    for (const record of recovered.compatible) session.origins.set(record.attempt, record.context);
    refreshRecoveryQueue(session);
  }
  const base = studentAvatarBase(student);
  session.data.profile = { nickname: student.alias, explorer: base, color: 'cyan', avatar: base ? createDefaultAvatar({ explorer: base }) : null, onboarded: !!base };
  active = session; notify();
  return () => { session.closed = true; session.queue = []; session.skills = {}; if (active === session) active = null; };
}
export async function saveStudentAstronaut(studentId, base) {
  if (!studentAvatarBase({ avatar: { base } })) throw new Error('Invalid astronaut');
  const session = active;
  const binding = getStudentPlaySession(studentId);
  if (!binding.isCurrent()) throw new Error('Student session changed');
  const response = await binding.request('/student-auth/avatar', {
    method: 'PATCH', body: JSON.stringify({ base, context: binding.context }),
  });
  if (!binding.isCurrent() || response?.student_id !== studentId || studentAvatarBase(response) !== base) {
    throw new Error('Student session changed');
  }
  session.student = { ...session.student, avatar: { base } };
  session.data.profile = { ...session.data.profile, explorer: base,
    avatar: createDefaultAvatar({ explorer: base }), onboarded: true };
  return response;
}
export async function flushStudentProgress({ hydrate = false } = {}) {
  const session = active;
  if (!session || session.closed) return;
  if (session.recovering) {
    if (session.conflict) throw new Error("Pending attempt needs review");
    return recoverAuthority(session);
  }
  return flushSession(session, hydrate);
}
async function flushSession(session, hydrate) {
  if (session.commercial) return flushRecoverySession(session, hydrate);
  if (session.flight) return session.flight;
  session.error = null;
  session.flight = (async () => {
    if (session.conflict) throw new Error('Pending attempt needs review');
    while (!session.closed && session.queue.length) {
      const attempt = session.queue[0];
      try {
        const record = persistPending(session.student.student_id, session.context, attempt);
        if (record.state === 'blocked') { session.conflict = true; throw new Error('Pending attempt needs review'); }
        session.storageError = false;
      } catch (error) { session.storageError = true; throw error; }
      let response;
      try {
        response = await session.request('/student/progress/attempts', { method: 'POST', body: JSON.stringify({ context: session.context, base_revision: session.revision, attempt }) });
      } catch (error) {
        if (error?.status === 409 || error?.status === 400 || error?.status === 422) {
          session.conflict = true;
          session.rejected.set(attempt.client_session_id, attempt);
          markPendingConflict(session.student.student_id, session.context, attempt);
        }
        throw error;
      }
      if (response?.student_id !== session.student.student_id || !response?.snapshot || response.context !== session.context || response.snapshot.schemaVersion !== 1 ||
          response.snapshot.gameContentVersion !== GAME_CONTENT_VERSION || !Number.isInteger(response.snapshot.revision) || response.snapshot.revision < Math.max(1, session.revision)) throw new Error('Invalid progress acknowledgement');
      // A late, valid A ACK may remove A's receipt; it never mutates B's memory.
      acknowledgePending(session.student.student_id, session.context, attempt);
      if (session.closed || active !== session) return;
      session.revision = response.snapshot.revision;
      session.queue.shift();
      if (hydrate) session.data = { ...snapshotToLocal(response.snapshot), profile: session.data.profile };
      // Hydrate only before mounting gameplay, never over an ongoing run.
    }
    try { ensureOutboxWritable(); session.storageError = false; }
    catch (error) { session.storageError = true; throw error; }
    session.full = !outboxHasRoom(session.student.student_id);
  })().catch(error => { if (!session.closed) session.error = error; throw error; }).finally(() => { session.flight = null; if (active === session) notify(); });
  notify(); return session.flight;
}
// ON only: original version-1 records keep their original keys and contexts.
// The context used on the wire is current; the educational attempt is unchanged.
function refreshRecoveryQueue(session) {
  const ramOnly = session.queue.filter(attempt => !session.origins.has(attempt));
  const restored = restorePending(session.student.student_id, session.context, { recoverPrevious: true });
  const records = [...restored.previous, ...restored.compatible];
  session.review = restored.invalid.length + records.filter(r => r.state === 'blocked').length;
  session.obsolete = 0; session.conflict = false;
  session.origins = new Map(records.filter(r => r.state === 'pending').map(r => [r.attempt, r.context]));
  session.queue = [...session.origins.keys(), ...ramOnly];
}
async function confirmOriginalReceipt(session, attempt) {
  const confirmation = await session.request('/student/progress/receipts/confirm', {
    method: 'POST', body: JSON.stringify({ context: session.context, attempt }),
  });
  if (confirmation?.student_id !== session.student.student_id || confirmation.context !== session.context ||
      confirmation.client_session_id !== attempt.client_session_id || typeof confirmation.confirmed !== 'boolean') {
    throw new Error('Invalid receipt confirmation');
  }
  return confirmation.confirmed ? confirmation : null;
}
function acknowledgeRecovery(session, origin, attempt, response, hydrate) {
  validateSnapshot(session.student.student_id, response, session.context);
  if (response.snapshot.revision < Math.max(1, session.revision)) throw new Error('Invalid progress acknowledgement');
  let hydrated;
  try { hydrated = snapshotToLocal(response.snapshot); }
  catch { throw new Error('Invalid progress acknowledgement'); }
  // Validate before deleting only the exact original slot, including a late A ACK.
  acknowledgePending(session.student.student_id, origin, attempt);
  if (session.closed || active !== session) return;
  session.revision = response.snapshot.revision;
  if (hydrate) session.data = { ...hydrated, profile: session.data.profile };
}
async function flushRecoverySession(session, hydrate) {
  session.needsHydration = !!hydrate || !!session.needsHydration;
  hydrate = session.needsHydration;
  if (session.flight) return session.flight;
  session.error = null;
  session.flight = (async () => {
    // Earlier clients may already have marked a historical lost ACK for review.
    // Such records may only be cleared by an exact receipt confirmation; they
    // never fall through to authorization or result creation.
    const restored = restorePending(session.student.student_id, session.context, { recoverPrevious: true });
    for (const record of [...restored.previous, ...restored.compatible].filter(r => r.state === 'blocked')) {
      if (session.closed || active !== session) return;
      const response = await confirmOriginalReceipt(session, record.attempt);
      if (response) acknowledgeRecovery(session, record.context, record.attempt, response, hydrate);
    }
    refreshRecoveryQueue(session);
    while (!session.closed && active === session && session.queue.length) {
      const attempt = session.queue[0];
      let origin = session.origins.get(attempt);
      if (!origin) {
        // RAM-only results after storage failure must become durable first.
        persistPending(session.student.student_id, session.context, attempt);
        origin = session.context; session.origins.set(attempt, origin);
      }
      try {
        let response;
        if (origin !== session.context) {
          // Only an explicit, authenticated "not found" permits grant verification.
          response = await confirmOriginalReceipt(session, attempt);
          if (!response) {
            if (session.closed || active !== session) return;
            const { client_session_id, game_content_version, planet_id, mission_index, mode } = attempt;
            const verified = await session.request('/student/play-authorizations/verify', {
              method: 'POST', body: JSON.stringify({ context: session.context, client_session_id,
                game_content_version, planet_id, mission_index, mode }),
            });
            if (session.closed || active !== session) return;
            if (verified?.verified !== true || verified.client_session_id !== attempt.client_session_id) {
              throw new Error('Invalid existing authorization response');
            }
          }
        }
        if (!response) {
          if (session.closed || active !== session) return;
          const current = readPending(session.student.student_id, origin, attempt);
          if (!current || current.state !== 'pending') { refreshRecoveryQueue(session); continue; }
          response = await session.request('/student/progress/attempts', { method: 'POST',
            body: JSON.stringify({ context: session.context, base_revision: session.revision, attempt }) });
        }
        acknowledgeRecovery(session, origin, attempt, response, hydrate);
        if (session.closed || active !== session) return;
      } catch (error) {
        if (!session.closed && active === session && [400, 403, 409, 422].includes(error?.status)) {
          markPendingConflict(session.student.student_id, origin, attempt);
          refreshRecoveryQueue(session);
        }
        throw error;
      }
      refreshRecoveryQueue(session);
    }
    if (session.closed || active !== session) return;
    if (hydrate) {
      // Another tab may have received the ACK and removed the same slot. Re-read
      // authority before mounting gameplay rather than hydrate a stale GET.
      const response = await session.request('/student/progress');
      validateSnapshot(session.student.student_id, response, session.context);
      if (session.closed || active !== session) return;
      if (response.snapshot.revision < session.revision) throw new Error('Invalid progress response');
      session.data = { ...snapshotToLocal(response.snapshot), profile: session.data.profile };
      session.revision = response.snapshot.revision;
      session.needsHydration = false;
    }
    try { ensureOutboxWritable(); session.storageError = false; }
    catch (error) { session.storageError = true; throw error; }
    if (session.review) throw new Error('Pending attempt needs review');
  })().catch(error => { if (!session.closed) session.error = error; throw error; }).finally(() => {
    session.flight = null; if (active === session) notify();
  });
  notify(); return session.flight;
}
// Recovery never reuses controls built from optimistic rejected state.
async function recoverAuthority(session) {
  if (session.recoveryFlight) return session.recoveryFlight;
  session.recoveryFlight = (async () => {
    if (session.flight) await session.flight.catch(() => {});
    if (session.closed || active !== session) return;
    if (session.conflict && !session.rejected.size) throw new Error('Pending attempt needs review');
    // Keep rejected IDs until every removal succeeds, including partial storage failures.
    for (const attempt of session.rejected.values()) acknowledgePending(session.student.student_id, session.context, attempt);
    const recovered = restorePending(session.student.student_id, session.context);
    const remaining = new Map(session.queue.filter(attempt => !session.rejected.has(attempt.client_session_id)).map(attempt => [attempt.client_session_id, attempt]));
    for (const record of recovered.compatible) if (!session.rejected.has(record.attempt.client_session_id)) remaining.set(record.attempt.client_session_id, record.attempt);
    session.queue = [...remaining.values()];
    session.rejected.clear(); session.conflict = false;
    // Recover RAM-only valid results durably before waiting on the next GET.
    for (const attempt of session.queue) persistPending(session.student.student_id, session.context, attempt);
    const response = await session.request('/student/progress');
    validateSnapshot(session.student.student_id, response, session.context);
    if (session.closed || active !== session) return;
    session.data = { ...snapshotToLocal(response.snapshot), profile: session.data.profile };
    session.revision = response.snapshot.revision; session.skills = {};
    await flushSession(session, true);
    if (session.closed || active !== session) return;
    session.generation += 1; session.recovering = false;
  })().catch(error => { if (!session.closed) session.error = error; throw error; }).finally(() => {
    session.recoveryFlight = null; if (active === session) notify();
  });
  notify(); return session.recoveryFlight;
}
export async function discardStudentProgressConflicts() {
  const session = active;
  if (!session) return;
  if (session.commercial) throw new Error('Pending attempts are retained for review');
  // Set before any storage/network operation: errors must never unblock stale gameplay.
  session.recovering = true; notify();
  try {
    for (const record of restorePending(session.student.student_id, session.context).compatible) {
      if (record.state === 'blocked') session.rejected.set(record.attempt.client_session_id, record.attempt);
    }
    return await recoverAuthority(session);
  } catch (error) { session.error = error; notify(); throw error; }
}
export function discardStudentProgressObsolete() {
  if (!active) return;
  if (active.commercial) throw new Error('Pending attempts are retained for review');
  try {
    discardObsolete(active.student.student_id, active.context);
    active.obsolete = 0; active.full = !outboxHasRoom(active.student.student_id);
  } catch (error) { active.storageError = true; active.error = error; throw error; }
  finally { notify(); }
}
export function finishProgressAttempt({ planetId, missionIndex, run, mode }) {
  if (!active || active.closed || run.devPreview) return;
  if (!isCurrentProgressAttempt(run.progressAttemptId)) return;
  const correct = run.correct || 0, wrong = run.wrong || 0, total = correct + wrong;
  const session = active;
  const attempt = { client_session_id: run.progressAttemptId || eventId(), game_content_version: GAME_CONTENT_VERSION,
    planet_id: planetId, mission_index: missionIndex, mode, score: mode === 'practice' ? 0 : (run.score || 0),
    correct, wrong, accuracy: total ? correct / total : 0, best_streak: run.bestStreak || 0,
    duration_ms: Math.min(3600000, Math.max(0, Math.round(run.durationMs ?? ((run.elapsedTime || 0) * 1000)))),
    won: !!run.won, completed_at: new Date().toISOString(), skill_attempts: Object.entries(active.skills).map(([tag, stats]) => ({ tag, ...stats })) };
  session.queue.push(attempt);
  if (run.progressAttemptId) session.playAttempt.finished = true;
  try {
    persistPending(session.student.student_id, session.context, attempt);
    if (session.commercial) session.origins.set(attempt, session.context);
  }
  catch (error) { session.storageError = true; session.error = error; notify(); return; }
  active.skills = {};
  flushStudentProgress().catch(() => {}); // Failure stays pending and visible, never reported saved.
}
const deviceMethods = new Set(['getSettings', 'setSettings', 'hasSeenTutorial', 'markTutorialSeen']);
const memory = { ...PlayerData, load: () => clone(active.data), save: data => { active.data = clone(data); } };
export const ProgressData = Object.fromEntries(Object.keys(PlayerData).map(name => [name, (...args) => {
  if (!active || deviceMethods.has(name)) return PlayerData[name](...args);
  if (name === 'getProfile') return clone(active.data.profile);
  if (name === 'hasProfile') return !!studentAvatarBase(active.student);
  if (name === 'saveProfile') throw new Error('School identity cannot edit a guest profile');
  const result = memory[name](...args);
  if (name === 'recordSkillAttempt' && args[2]) {
    const [,,tag,correct] = args;
    const stat = active.skills[tag] || { correct: 0, wrong: 0 };
    stat[correct ? 'correct' : 'wrong'] += 1; active.skills[tag] = stat;
  }
  return result;
}]));
ProgressData.beginAttempt = ticket => {
  if (ticket && !ticket.isCurrent()) throw new Error('Student session changed');
  if (!active) return undefined; // Guest remains entirely unchanged.
  active.skills = {};
  active.playAttempt = { id: ticket?.id || eventId(), generation: active.generation, finished: false };
  return active.playAttempt.id;
};

// Practice did not write guest skill history; preserve that contract.
export function recordStudentSkillAttempt(...args) {
  if (active) return ProgressData.recordSkillAttempt(...args);
}
